import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useGetAgendaRecordQuery } from 'store/services/flow/agendaRecords';
import { useGetModulesWithAgendasQuery } from 'store/services/core/modules';
import { useGetTemplateByIdQuery } from 'store/services/helios/templates';
import { useCallback, useContext } from 'react';
import { useMatches, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginContext } from 'contexts/LoginProvider';
import { type RouteHandle } from 'contexts/BreadcrumbsProvider';
import { resolveAgendaName } from 'utils/resolveAgendaName';
import { resolveHeliosAgendaName } from 'utils/helios/resolveHeliosAgendaName';

export const useGetRouteLabel = () => {
  const { id: moduleId = '', childId = '', agendaId = '', recordId = '' } = useParams();
  const matches = useMatches();
  const systemType = (matches.find((match) => (match.handle as RouteHandle)?.systemType)?.handle as RouteHandle)?.systemType;
  const { t, i18n } = useTranslation();

  const { user } = useContext(LoginContext);

  const { data: modulesData } = useGetModulesWithAgendasQuery(
    {
      takeAgendas: 0,
      takeModules: 0,
    },
    /*
     * If ever extending the Provider to work also with admin routes,
     * need to change this to !user || !isAdmin. The skip is necessary
     * to prevent fetching data for unauthenticated users.
     */
    {
      skip: !user,
    }
  );

  const agendaOverviewFromModules = modulesData?.modules
    ?.find((m) => m.moduleId === (childId || moduleId))
    ?.agendas?.find((a) => a.agendaId === agendaId);

  const agendaNameFromModules = agendaOverviewFromModules ? resolveAgendaName(agendaOverviewFromModules) : undefined;

  const shouldFetchAgendaBackup = agendaId && !agendaNameFromModules && systemType === 'Flow';
  const shouldFetchTemplateBackup = agendaId && !agendaNameFromModules && systemType === 'Helios';

  const { data: agendaData } = useGetAgendaByIdQuery(
    { id: agendaId },
    {
      skip: !shouldFetchAgendaBackup,
    }
  );

  const { data: templateData } = useGetTemplateByIdQuery(
    { id: agendaId },
    {
      skip: !shouldFetchTemplateBackup,
    }
  );

  const { data: recordData } = useGetAgendaRecordQuery(
    { agendaId, recordId },
    {
      skip: !agendaId || !recordId || recordId === 'new' || systemType !== 'Flow',
    }
  );

  const getLabel = useCallback(
    (handleSegmentType: string): React.ReactNode => {
      if (!modulesData) return t('common.loading');

      const module = modulesData.modules.find((m) => m.moduleId === moduleId);

      if (agendaId && handleSegmentType === 'agenda') {
        let agendaOverview: { displayAs?: string; name?: string } | undefined;

        if (childId) {
          const childModule = modulesData?.modules?.find((c) => c.moduleId === childId);
          agendaOverview = childModule?.agendas.find((a) => a.agendaId === agendaId);
        } else {
          agendaOverview = module?.agendas.find((a) => a.agendaId === agendaId);
        }

        let agendaName: string | undefined;

        if (systemType === 'Flow' && agendaData?.id === agendaId) {
          agendaName = resolveAgendaName(agendaData, resolveAgendaName(agendaOverview));
        } else if (systemType === 'Helios' && templateData?.id === agendaId) {
          agendaName = resolveHeliosAgendaName(i18n, templateData, resolveHeliosAgendaName(i18n, agendaOverview));
        } else {
          agendaName = resolveAgendaName(agendaOverview);
        }

        return agendaName || t('common.agenda');
      }

      if (childId && handleSegmentType === 'childModule') {
        const childModule = modulesData?.modules?.find((c) => c.moduleId === childId);
        return childModule?.name || t('common.childModule');
      }

      if (moduleId && handleSegmentType === 'module') {
        return module?.name || t('table.header.module');
      }

      if (handleSegmentType === 'record') {
        const isCurrentRecord = recordData?.record?.id === recordId && recordData?.record?.agendaId === agendaId;
        const identifier = isCurrentRecord ? recordData?.record?.identifier?.trim() : undefined;
        return identifier ? identifier : null;
      }

      return handleSegmentType;
    },
    [
      agendaData,
      agendaId,
      childId,
      i18n,
      moduleId,
      modulesData,
      recordData?.record?.agendaId,
      recordData?.record?.id,
      recordData?.record?.identifier,
      recordId,
      systemType,
      t,
      templateData,
    ]
  );

  return { getLabel };
};
